import {CheckCircleIcon, ExclamationCircleIcon, XMarkIcon} from "@heroicons/react/20/solid";
import {
  AttachmentTarget,
  AttachmentTypeCode,
  AttachmentUploadMetadata,
} from "@ots-frontend/ots-data";
import {OtsIconActionButton} from "../components/OtsButton";
import {attachmentLabel, formatAttachmentSize} from "./attachment-utils";

export type AttachmentUploadStatus = "ready" | "uploading" | "complete" | "error";

export type AttachmentUploadDraft = {
  id: string;
  file: File;
  target: AttachmentTarget;
  metadata: AttachmentUploadMetadata;
  status: AttachmentUploadStatus;
  error?: string;
};

export type AttachmentUploadQueueProps = {
  items: AttachmentUploadDraft[];
  targets: AttachmentTarget[];
  attachmentTypes: AttachmentTypeCode[];
  onChange: (id: string, changes: Partial<Pick<AttachmentUploadDraft, "target" | "metadata">>) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
};

const fieldClassName = "min-h-9 w-full rounded-lg border border-[var(--ots-field-outline)] bg-[var(--ots-field)] px-2.5 text-sm text-[var(--ots-text)] outline-none focus:border-[var(--ots-primary)] focus:ring-2 focus:ring-[var(--ots-primary-container)] disabled:opacity-60";

/** Metadata editor for files waiting to be uploaded. */
export const AttachmentUploadQueue = ({
  items,
  targets,
  attachmentTypes,
  onChange,
  onRemove,
  disabled = false,
}: AttachmentUploadQueueProps) => {
  if (!items.length) return null;

  return (
    <section aria-labelledby="attachment-queue-heading" className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 id="attachment-queue-heading" className="text-sm font-semibold text-[var(--ots-secondary)]">
          Ready to upload ({items.length})
        </h3>
      </div>
      <ul className="space-y-2">
        {items.map((item) => {
          const locked = disabled || item.status === "uploading" || item.status === "complete";
          return (
            <li key={item.id} className="rounded-xl border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] p-3">
              <div className="flex min-w-0 items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <p className="max-w-full truncate text-sm font-semibold text-[var(--ots-text)]" title={item.file.name}>
                      {item.file.name}
                    </p>
                    <span className="text-xs text-[var(--ots-text-muted)]">{formatAttachmentSize(item.file.size)}</span>
                    {item.status === "uploading" && <span className="text-xs font-medium text-[var(--ots-primary)]">Uploading…</span>}
                    {item.status === "complete" && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--ots-success)]">
                        <CheckCircleIcon className="size-4" aria-hidden="true"/> Uploaded
                      </span>
                    )}
                  </div>

                  <div className={targets.length > 1 ? "mt-3 grid gap-2 sm:grid-cols-3" : "mt-3 grid gap-2 sm:grid-cols-2"}>
                    {targets.length > 1 && (
                      <label className="text-xs font-medium text-[var(--ots-text-muted)]">
                        Attach to
                        <select
                          className={`${fieldClassName} mt-1`}
                          aria-label={`Attach ${item.file.name} to`}
                          value={`${item.target.objectType}:${item.target.objectId}`}
                          disabled={locked}
                          onChange={(event) => {
                            const target = targets.find((candidate) => `${candidate.objectType}:${candidate.objectId}` === event.target.value);
                            if (target) onChange(item.id, {target});
                          }}>
                          {targets.map((target) => (
                            <option key={`${target.objectType}:${target.objectId}`} value={`${target.objectType}:${target.objectId}`}>
                              {target.label ?? `${target.objectType} ${target.objectId}`}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label className="text-xs font-medium text-[var(--ots-text-muted)]">
                      Category
                      <select
                        className={`${fieldClassName} mt-1`}
                        aria-label={`Category for ${item.file.name}`}
                        value={item.metadata.attachmentType}
                        disabled={locked}
                        onChange={(event) => onChange(item.id, {
                          metadata: {...item.metadata, attachmentType: event.target.value as AttachmentTypeCode},
                        })}>
                        {attachmentTypes.map((type) => <option key={type} value={type}>{attachmentLabel(type)}</option>)}
                      </select>
                    </label>
                    <label className="text-xs font-medium text-[var(--ots-text-muted)]">
                      Description <span className="font-normal">(optional)</span>
                      <input
                        className={`${fieldClassName} mt-1`}
                        aria-label={`Description for ${item.file.name}`}
                        value={item.metadata.description ?? ""}
                        disabled={locked}
                        maxLength={500}
                        placeholder="What is this file?"
                        onChange={(event) => onChange(item.id, {
                          metadata: {...item.metadata, description: event.target.value},
                        })}/>
                    </label>
                  </div>
                  {item.error && (
                    <p role="alert" className="mt-2 flex items-center gap-1.5 text-xs font-medium text-[var(--ots-error)]">
                      <ExclamationCircleIcon aria-hidden="true" className="size-4"/>{item.error}
                    </p>
                  )}
                </div>
                <OtsIconActionButton
                  size="compact"
                  variant="text"
                  icon={<XMarkIcon/>}
                  label={`Remove ${item.file.name}`}
                  disabled={item.status === "uploading"}
                  onClick={() => onRemove(item.id)}/>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
